const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || 'https://auth.nuxarisapiv1.xyz';

/* ─────────────────────────── Session ───────────────────────────
 *
 * The access token lives 15 minutes; the refresh token renews it. Three rules, each the fix for
 * a member being sent back to the EMAIL screen instead of the password (06/10: « ça met du temps
 * à charger… en WiFi, en 4G non ») :
 *
 *  1. ONE refresh at a time. Every request that found the token expired used to call /refresh on
 *     its own — a dozen at once when the wallet's polls all expired together — and the auth API
 *     allows a few per minute. Concurrent callers now wait for the same answer.
 *  2. Refresh BEFORE expiry, not after a refusal. A request about to carry an expired token
 *     refreshes first, so app start costs one round trip instead of three (/me refused, /refresh,
 *     /me again) — which is most of the wait on a slow connection.
 *  3. Only a refusal of the TOKEN ends the session (400/401 from /refresh). A 429, a 5xx or a
 *     dropped connection used to wipe both tokens — a rate limit then logged the member out. Now
 *     the session survives, and refreshes pause a few seconds instead of hammering the limit.
 */

export type RefreshResult = 'ok' | 'invalid' | 'unavailable';

/** Refresh this many seconds before the token expires: a request in flight should not expire. */
const EARLY_S = 30;
/** After an unavailable refresh (429, 5xx, network), wait this long before asking again. */
const BACKOFF_MS = 5_000;

let refreshing: Promise<RefreshResult> | null = null;
let backoffUntil = 0;

/** The `exp` claim of a JWT, in seconds, or null when it cannot be read. */
function tokenExp(token: string): number | null {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(part)).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return 'invalid';
  try {
    const res = await fetch(`${AUTH_API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (res.status === 400 || res.status === 401) {
      // The server refused the session itself (expired, revoked, account closed): it is over.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return 'invalid';
    }
    if (!res.ok) return 'unavailable';
    const data = await res.json();
    if (!data?.access_token) return 'unavailable';
    localStorage.setItem('access_token', data.access_token);
    return 'ok';
  } catch {
    return 'unavailable';
  }
}

/** Renews the access token. Concurrent callers share one request; see the rules above. */
export function refreshSession(): Promise<RefreshResult> {
  if (refreshing) return refreshing;
  if (Date.now() < backoffUntil) return Promise.resolve('unavailable');
  refreshing = doRefresh()
    .then((r) => {
      if (r === 'unavailable') backoffUntil = Date.now() + BACKOFF_MS;
      return r;
    })
    .finally(() => { refreshing = null; });
  return refreshing;
}

/** The access token to send now — renewed first when it is about to expire. */
export async function freshAccessToken(): Promise<string | null> {
  const token = localStorage.getItem('access_token');
  if (!token || !localStorage.getItem('refresh_token')) return token;
  const exp = tokenExp(token);
  if (exp !== null && exp - Date.now() / 1000 < EARLY_S) {
    await refreshSession();
    return localStorage.getItem('access_token');
  }
  return token;
}

// Accepts either a path on the auth API or an absolute URL (e.g. bridge API).
// Always attaches the access token and transparently retries once after a
// token refresh on 401.
export async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${AUTH_API_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  const attach = !headers['Authorization'];
  const accessToken = attach ? await freshAccessToken() : null;
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && accessToken) {
    if ((await refreshSession()) === 'ok') {
      headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
      return fetch(url, { ...options, headers });
    }
  }

  return res;
}

/* ─────────────────────────── Lien de compte X ─────────────────────────── */

/**
 * Demarre le rattachement : le serveur cree l'etat PKCE et rend l'URL d'autorisation X.
 *
 * ⚠️ On QUITTE l'app plutot que d'ouvrir une fenetre. Un `window.open` est avale par le
 * bloqueur de pop-ups sur mobile — precisement la ou la plupart des membres sont — et un
 * bouton qui ne fait rien une fois sur deux est pire qu'un detour assume. X nous renvoie
 * ensuite sur /rewards avec le resultat en parametre.
 */
export async function startXLink(): Promise<string> {
  const res = await authFetch('/api/auth/x/start');
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not start');
  return (await res.json()).url;
}

export async function unlinkX(): Promise<void> {
  const res = await authFetch('/api/auth/x/unlink', { method: 'POST' });
  if (!res.ok) throw new Error('Could not unlink');
}

export { AUTH_API_URL };
